import readings from './oudekerkSouthRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { ReservedFootprint } from '../types';
import { lotFrame } from '../lotFrame';
import { obliqueCorners } from '../lotFootprint';
import { measureLine } from '../measuredLine';
import { obliqueRowLots, type ObliqueRow } from './obliqueRowLots';
import { intersection } from '../../../geo/clip';
import { oudekerkYard, planOudeKerkPleinFronts } from './oudekerkYardPlan';

export const OUDEKERK_SOUTH_ROWS = readings.rows as unknown as readonly ObliqueRow[];

/** Only the read canal plots and their small rear margins, not the unrecorded inner district. */
export function planOudeKerkSouthFronts(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  const ids = [
    'quay-oudezijds-voorburgwal-2',
    'street-sint-jansstraat',
    'street-leidekkerssteeg',
    'street-gooijersteeg',
    'street-blaauwlakensteeg',
    'street-zwartlakensteeg',
    'street-sint-annenstraat',
    'street-dollebegijnensteeg',
    'street-trompetterssteeg',
    'street-oudekerksplein',
  ];
  if (ids.some((id) => !ctx.layout.streets.some((s) => s.id === id)))
    return { lots: [], reserved: [], unresolved: [] };
  const plein = planOudeKerkPleinFronts(ctx, buildable, reserved);
  const lots = obliqueRowLots(
    OUDEKERK_SOUTH_ROWS,
    ctx.layout,
    buildable,
    [...reserved, ...plein.reserved],
    30_000
  );
  const built = new Set(lots.map((l) => l.reconstruction!.id));
  const unresolved = OUDEKERK_SOUTH_ROWS.flatMap((r) =>
    r.houses.flatMap((h) => h.plots?.map((p) => p.id) ?? [h.id]).filter((id) => !built.has(id))
  );
  const plots: ReservedFootprint[] = lots.map((l) => {
    const { frontMid, facing, width, depth, shear = 0 } = lotFrame(l);
    return {
      id: `surveyed:${l.reconstruction!.id}`,
      polygon: { outer: [...obliqueCorners(frontMid, facing, width, depth + 2, shear)], holes: [] },
    };
  });
  // Trees/open ground beside the church are visible in both sources. These chainage/depth
  // bounds are an estimated margin fitted to the retained street, not a traced garden parcel.
  const source = ctx.layout.streets.find((s) => s.id === ids[0])!;
  const line = measureLine(
    source.centerline,
    source.centerline.map(() => source.width)
  );
  const a = line.pointAt(416),
    b = line.pointAt(433),
    mid = line.pointAt(424.5);
  const inward = [-mid.tangent[1], mid.tangent[0]] as const;
  const front = [mid.point[0] + inward[0] * 2.57, mid.point[1] + inward[1] * 2.57] as const;
  const margin = {
    outer: [
      ...obliqueCorners(
        front,
        [-inward[0], -inward[1]],
        Math.hypot(b.point[0] - a.point[0], b.point[1] - a.point[1]),
        24,
        -0.22
      ),
    ],
    holes: [],
  };
  const yard = oudekerkYard(ctx.layout);
  for (const polygon of yard ? intersection([margin], [{ outer: yard, holes: [] }]) : [margin])
    plots.push({ id: 'surveyed-oudekerk-south-open-margin', polygon });
  return {
    lots: [...lots, ...plein.lots],
    reserved: [...plots, ...plein.reserved],
    unresolved: [...unresolved, ...plein.unresolved],
  };
}
